import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ContactPage() {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const id = 'schema-contact';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: import.meta.env.VITE_SITE_URL },
            { '@type': 'ListItem', position: 2, name: 'Contact', item: `${import.meta.env.VITE_SITE_URL}/contact` },
          ],
        },
        {
          '@type': 'ContactPage',
          '@id': `${import.meta.env.VITE_SITE_URL}/contact#webpage`,
          url: `${import.meta.env.VITE_SITE_URL}/contact`,
          name: 'Contact MAX Sails Canada | Request a Custom Sail Quote',
          description: 'Contact MAX Sails Canada for custom sail making, sail repair, in-stock ILCA sails, and volume orders. Get a quote by phone or email.',
          inLanguage: ['en', 'fr'],
          isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL}/#website` },
          about: { '@id': `${import.meta.env.VITE_SITE_URL}/#organization` },
          dateModified: '2026-04-09',
        },
        {
          '@type': 'LocalBusiness',
          '@id': `${import.meta.env.VITE_SITE_URL}/#organization`,
          name: 'MAX Sails Canada',
          telephone: '+18193031365',
          email: 'contact@MAXSails.ca',
          url: import.meta.env.VITE_SITE_URL,
          address: {
            '@type': 'PostalAddress',
            addressRegion: 'QC',
            addressCountry: 'CA',
          },
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: '+18193031365',
            email: 'contact@MAXSails.ca',
            contactType: 'customer service',
            availableLanguage: ['English', 'French'],
            areaServed: 'Worldwide',
          },
        },
      ],
    });
    return () => {
      const s = document.getElementById(id);
      if (s) s.remove();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d79de4adqn9mib2tr450', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-20">
        {/* Hero */}
        <section className="relative w-full h-[280px] flex items-center justify-center overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/5055d0a9a6722118f604e4f938e2ca3d.jpg"
            alt="Contact MAX Sails Canada"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative z-10 text-center px-6">
            <p className="text-red-400 text-xs uppercase tracking-[0.3em] font-semibold mb-3">{t('contact_hero_label')}</p>
            <h1 className="text-white font-black text-4xl md:text-6xl" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {t('contact_hero_title')}
            </h1>
          </div>
        </section>

        {/* Contact Content */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex flex-col lg:flex-row gap-16">
              {/* Info */}
              <div className="w-full lg:w-2/5">
                <p className="text-red-600 text-xs uppercase tracking-[0.3em] font-semibold mb-3">{t('contact_reach_label')}</p>
                <h2 className="text-3xl font-black text-gray-900 mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  {t('contact_reach_title_1')}<br /><span className="text-red-600">{t('contact_reach_title_2')}</span>
                </h2>
                <p className="text-gray-500 text-sm leading-relaxed mb-8">{t('contact_reach_desc')}</p>
                <div className="space-y-5">
                  {[
                    { icon: 'ri-phone-line', labelKey: 'contact_phone_label', value: '+1 (819) 303-1365', href: 'tel:+18193031365' },
                    { icon: 'ri-mail-line', labelKey: 'contact_email_label', value: 'contact@MAXSails.ca', href: 'mailto:contact@MAXSails.ca' },
                  ].map((item) => (
                    <div key={item.labelKey} className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-50 flex-shrink-0">
                        <i className={`${item.icon} text-red-600`} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">{t(item.labelKey)}</p>
                        <a href={item.href} className="text-gray-800 text-sm hover:text-red-600 transition-colors cursor-pointer">{item.value}</a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Form */}
              <div className="w-full lg:w-3/5">
                {submitted ? (
                  <div className="flex flex-col items-center justify-center h-full py-20 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mb-5">
                      <i className="ri-check-line text-green-600 text-2xl" />
                    </div>
                    <h3 className="text-2xl font-black text-gray-900 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('contact_success_title')}</h3>
                    <p className="text-gray-500 text-sm">{t('contact_success_desc')}</p>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="bg-stone-50 rounded-2xl p-8 md:p-10 space-y-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">{t('contact_fname')}</label>
                        <input name="first_name" required type="text" placeholder="John" className="w-full px-4 py-3 rounded-lg border border-stone-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">{t('contact_lname')}</label>
                        <input name="last_name" required type="text" placeholder="Doe" className="w-full px-4 py-3 rounded-lg border border-stone-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">{t('contact_email_field')}</label>
                      <input name="email" required type="email" placeholder="john@example.com" className="w-full px-4 py-3 rounded-lg border border-stone-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">{t('contact_phone_field')}</label>
                      <input name="phone" type="tel" placeholder="+1 (613) 000-0000" className="w-full px-4 py-3 rounded-lg border border-stone-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">{t('contact_service')}</label>
                      <select name="service" className="w-full px-4 py-3 rounded-lg border border-stone-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors cursor-pointer">
                        <option value="">{t('contact_service_placeholder')}</option>
                        <option value="Custom Sail">{t('contact_service_1')}</option>
                        <option value="In Stock Sails">{t('contact_service_2')}</option>
                        <option value="Volume Order">{t('contact_service_3')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">{t('contact_message')}</label>
                      <textarea
                        name="message"
                        required
                        rows={5}
                        maxLength={500}
                        placeholder={t('contact_message_placeholder')}
                        className="w-full px-4 py-3 rounded-lg border border-stone-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-red-400 transition-colors resize-none"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 rounded-lg bg-red-600 text-white font-semibold text-sm hover:bg-red-700 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                    >
                      {loading ? t('contact_sending') : t('contact_submit')}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
